import React, { useCallback, memo, useMemo, useEffect, type RefCallback } from 'react';
import Popup, { type TriggerProps } from '@atlaskit/popup';
import { Box, Flex, xcss } from '@atlaskit/primitives';
import { useEntryPoint } from '@atlassian/jira-entry-point';
import { useEntryPointButtonTrigger } from '@atlassian/jira-entry-point-button-trigger';
import { JiraEntryPointContainer } from '@atlassian/jira-entry-point-container';
import { useCloudId } from '@atlassian/jira-tenant-context-controller';
import type { Goal } from '../common/types';
import { LoadingContent } from '../common/ui/loading-content';
import { GoalsCellContent } from './goals-cell-content';
import { goalPickerPopoverEntryPoint } from './goals-popup-content/entrypoint';

const POPUP_OFFSET: [number, number] = [-8, 8];

export type GoalPickerProps = {
	issueAri: string;
	goals: Goal[];
	goalCount: number;
	isEditable: boolean;
	editMode: boolean;
	onClose: () => void;
	onGoalPicked: (goal: Goal) => void;
	onGoalUnlinked: (goalUnlinkedId: string, remainingGoals: Goal[]) => void;
	shouldRenderToParent?: boolean;
	showPlaceholder?: boolean;
};

const GoalsPicker = ({
	issueAri,
	goals,
	goalCount,
	isEditable,
	editMode,
	onClose,
	onGoalPicked,
	onGoalUnlinked,
	shouldRenderToParent = false,
	showPlaceholder,
}: GoalPickerProps) => {
	const cloudId = useCloudId();
	const { entryPointActions, entryPointReferenceSubject } = useEntryPoint(
		goalPickerPopoverEntryPoint,
		{ cloudId },
	);
	const buttonTrigger = useEntryPointButtonTrigger(entryPointActions);

	useEffect(() => {
		editMode && entryPointActions.load();
	}, [editMode, entryPointActions]);

	const popupTrigger = useCallback(
		(triggerProps: TriggerProps) => (
			<Box
				role="button"
				xcss={triggerStyles}
				// eslint-disable-next-line react/jsx-props-no-spreading
				{...triggerProps}
				ref={(element: HTMLElement) => {
					buttonTrigger(element);
					// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
					(triggerProps.ref as RefCallback<HTMLElement>)(element);
				}}
			>
				<GoalsCellContent
					showPlaceholder={showPlaceholder}
					goals={goals}
					goalCount={goalCount}
					isEditable={isEditable}
				/>
			</Box>
		),

		[buttonTrigger, goalCount, goals, isEditable, showPlaceholder],
	);

	const runtimeProps = useMemo(
		() => ({
			issueAri,
			goalCount,
			onGoalPicked,
			onGoalUnlinked,
			onClose,
		}),
		[goalCount, issueAri, onClose, onGoalPicked, onGoalUnlinked],
	);
	const content = useCallback(
		() => (
			<JiraEntryPointContainer
				entryPointReferenceSubject={entryPointReferenceSubject}
				id="jwm.goals.picker.edit.popup"
				packageName="jiraBusinessGoalPicker"
				runtimeProps={runtimeProps}
				fallback={<LoadingContent />}
				errorFallback="flag"
			/>
		),
		[entryPointReferenceSubject, runtimeProps],
	);

	return (
		<Flex xcss={goalPickerStyles} alignItems="center" direction="column">
			<Popup
				isOpen={editMode}
				content={content}
				trigger={popupTrigger}
				offset={POPUP_OFFSET}
				placement="bottom-start"
				shouldRenderToParent={shouldRenderToParent}
				onClose={onClose}
			/>
		</Flex>
	);
};

export default memo(GoalsPicker);

const triggerStyles = xcss({
	display: 'flex',
	height: '100%',
	width: '100%',
	flexGrow: 1,
	flexDirection: 'column',
	alignItems: 'center',
});

const goalPickerStyles = xcss({
	minHeight: '100%',
	width: '100%',
});
